/* eslint-disable max-lines */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from "react";
import { useMutation, ApolloProvider as ApolloHookProvider } from "@apollo/client";
import { gql } from "graphql-tag";
import { Grid } from "@material-ui/core";
import TabSwitch from "components/tab-switch";
import { Button, Modal } from "components/haven-design";
import { Viewer } from "components/auth/types";
import {
  ChangeInterestStage,
  ChangeInterestStageVariables,
  createActivityContainer,
  createActivityContainerVariables,
  QueryInterestProfile_interest as InterestType,
  logCall as logCallType,
  logCallVariables,
  SendMessagesProfile,
  SendMessagesProfileVariables,
  TemplateType,
} from "types/gql-types";
import { html } from "components/send-messages/submit-handler";
import { emptyDelta } from "components/message-editor";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { apolloClient } from "data/graphql";
import { Action } from "pages/dashboard/interests-profile/components/reason-modal";
import { DialogActions, DialogContent } from "components/haven-design/modal/dialog";
import { CREATE_ACTIVITY, SEND_MESSAGE, LOG_CALL } from "../interests-activities/queries";
import ChangeStatusForm, { ChangeStatusStateValues } from "./change-status-form";
import MessageForm, { SendMessageStateValues } from "./message-form";
import NoteForm, { AddNoteStateValues } from "./note-form";
import LogCallForm, { LogCallStateValues } from "./log-call-form";

const MUTATION_CHANGE_STATUS = gql`
  mutation ChangeInterestStage(
    $interest: ID!,
    $status: String!,
    $reason: String
    $actionDate: String,
    $unitNumber: String
  ) {
    changeInterestStatus(
      interestId: $interest,
      status: $status,
      reason: $reason
      actionDate: $actionDate
      unitNumber: $unitNumber
    )
  }
`;

interface Props {
  focusedAction?: Action;
  interestID: string;
  interestListID: string;
  viewer: Viewer;
  interest: InterestType;

  // This handler prop is needed if the user clicks the same action call more than once in a row.
  setBatchActionsModalFocusedAction: React.Dispatch<React.SetStateAction<undefined>>;
}

const initialStatusState = {
  actionDate: null,
  reason: "",
  status: "",
  unitNumber: null,
} as ChangeStatusStateValues;

const initialNoteState = { note: "" } as AddNoteStateValues;

const initialLogCallState = {
  reason: "",
  outcome: "",
  note: "",
} as LogCallStateValues;

const initialMessageState = {
  emailEnabled: true,
  emailBody: emptyDelta,
  emailSubject: "",
  emailBccAddress: "",
  emailRespondToEnabled: false,
  letterEnabled: true,
  letterBody: emptyDelta,
  smsEnabled: false,
  smsBody: emptyDelta,
  smsPropertyUseCaseId: null,
} as SendMessageStateValues;

const errorBannerHideAfterMs = 5000;

const BatchActionsModal: React.FC<Props> = ({
  focusedAction,
  interestID,
  interestListID,
  viewer,
  interest,
  setBatchActionsModalFocusedAction,
}) => {
  const { openSnackbar } = useSnackbar();

  const [changeStage] =
  useMutation<ChangeInterestStage, ChangeInterestStageVariables>(MUTATION_CHANGE_STATUS, {
    client: apolloClient,
    refetchQueries: ["getActivityFeed"],
  });

  const [createActivity] = useMutation<createActivityContainer, createActivityContainerVariables>(
    CREATE_ACTIVITY,
    {
      client: apolloClient,
      refetchQueries: ["getActivityFeed"],
      variables: { data: {} },
    },
  );

  const [logCall] = useMutation<logCallType, logCallVariables>(LOG_CALL, {
    refetchQueries: ["getActivityFeed"],
    client: apolloClient,
  });

  const [sendTemplateMessages] =
  useMutation<SendMessagesProfile, SendMessagesProfileVariables>(SEND_MESSAGE, {
    refetchQueries: ["getActivityFeed"],
    client: apolloClient,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [myFocusedAction, setMyFocusedAction] = useState(Action.Log_Call);

  const [disableSubmitControls, setDisableSubmitControls] = useState<boolean>(false);

  const [switchLogCallIsOn, setSwitchLogCallIsOn] = useState(false);
  const [switchAddNoteIsOn, setSwitchAddNoteIsOn] = useState(false);
  const [switchSendMsgIsOn, setSwitchSendMsgIsOn] = useState(false);
  const [switchChangeStatusIsOn, setSwitchChangeStatusIsOn] = useState(false);

  const [changeStatusStateValues, setChangeStatusStateValues] = useState(initialStatusState);
  const [addNoteStateValues, setAddNoteStateValues] = useState(initialNoteState);
  const [logCallStateValues, setLogCallStateValues] = useState(initialLogCallState);
  const [sendMessageStateValues, setSendMessageStateValues] = useState(initialMessageState);

  const [tabHasErrorSendMsg, setTabHasErrorSendMsg] = useState(false);
  const [tabHasErrorLogCall, setTabHasErrorLogCall] = useState(false);
  const [tabHasErrorAddNote, setTabHasErrorAddNote] = useState(false);
  const [tabHasErrorChangeStatus, setTabHasErrorChangeStatus] = useState(false);

  const [errorMsgSendMsgEmail, setErrorMsgSendMsgEmail] = useState("");
  const [errorMsgSendMsgSms, setErrorMsgSendMsgSms] = useState("");
  const [errorMsgSendMsgLetter, setErrorMsgSendMsgLetter] = useState("");
  const [errorMsgLogCall, setErrorMsgLogCall] = useState("");
  const [errorMsgAddNote, setErrorMsgAddNote] = useState("");
  const [errorMsgChangeStatus, setErrorMsgChangeStatus] = useState("");

  const onChangeFocusedAction = () => {
    if (!focusedAction) {
      return;
    }

    setMyFocusedAction(focusedAction);
    setSwitchLogCallIsOn(focusedAction === Action.Log_Call);
    setSwitchAddNoteIsOn(focusedAction === Action.Add_Note);
    setSwitchSendMsgIsOn(focusedAction === Action.Send_Message);
    setIsOpen(true);
  };

  useEffect(onChangeFocusedAction, [focusedAction]);

  const renderActionsViews = (action: Action | undefined) => (
    <ApolloHookProvider client={apolloClient}>
      <Grid item xs={12}>
        <LogCallForm
          phones={[
            {
              type: interest.householdMember0PrimaryPhoneType || "Phone",
              number: interest.householdMember0PrimaryPhone || "",
            },
          ]}
          onChange={(stateValues: LogCallStateValues) => {
            setSwitchLogCallIsOn(true);
            setLogCallStateValues(stateValues);
          }}
          isShowing={action === Action.Log_Call}
          errorMsg={errorMsgLogCall}
        />
      </Grid>
      <MessageForm
        interest={interest}
        interestListID={interestListID}
        viewer={viewer}
        onChange={(stateValues: SendMessageStateValues) => {
          setSwitchSendMsgIsOn(true);
          setSendMessageStateValues(stateValues);
        }}
        isShowing={action === Action.Send_Message}
        errorMsgEmail={errorMsgSendMsgEmail}
        errorMsgSms={errorMsgSendMsgSms}
        errorMsgLetter={errorMsgSendMsgLetter}
      />
      <Grid item xs={12}>
        <NoteForm
          isLoading={false}
          onChange={(stateValues: AddNoteStateValues) => {
            setSwitchAddNoteIsOn(true);
            setAddNoteStateValues(stateValues);
          }}
          isShowing={action === Action.Add_Note}
          errorMsg={errorMsgAddNote}
        />
      </Grid>
      <Grid item xs={12}>
        <ChangeStatusForm
          interest={interest}
          onChange={(stateValues: Partial<ChangeStatusStateValues>) => {
            setSwitchChangeStatusIsOn(true);
            setChangeStatusStateValues({ ...changeStatusStateValues, ...stateValues });
          }}
          isShowing={action === Action.Change_Status}
          errorMsg={errorMsgChangeStatus}
          state={changeStatusStateValues}
        />
      </Grid>
    </ApolloHookProvider>
  );

  const renderTabsView = (action: Action | undefined) => (
    <>
      <TabSwitch
        id="log-call-switch"
        tabIsActive={action === Action.Log_Call}
        switchIsOn={switchLogCallIsOn}
        onSwitchClick={() => setSwitchLogCallIsOn(!switchLogCallIsOn)}
        onTabClick={() => setMyFocusedAction(Action.Log_Call)}
        label="Log a Call"
        tabHasError={tabHasErrorLogCall}
      />

      <TabSwitch
        id="send-message-switch"
        tabIsActive={action === Action.Send_Message}
        switchIsOn={switchSendMsgIsOn}
        onSwitchClick={() => setSwitchSendMsgIsOn(!switchSendMsgIsOn)}
        onTabClick={() => setMyFocusedAction(Action.Send_Message)}
        label="Send Message"
        tabHasError={tabHasErrorSendMsg}
      />

      <TabSwitch
        id="add-note-switch"
        tabIsActive={action === Action.Add_Note}
        switchIsOn={switchAddNoteIsOn}
        onSwitchClick={() => setSwitchAddNoteIsOn(!switchAddNoteIsOn)}
        onTabClick={() => setMyFocusedAction(Action.Add_Note)}
        label="Add a Note"
        tabHasError={tabHasErrorAddNote}
      />

      <TabSwitch
        id="change-status-switch"
        tabIsActive={action === Action.Change_Status}
        switchIsOn={switchChangeStatusIsOn}
        onSwitchClick={() => setSwitchChangeStatusIsOn(!switchChangeStatusIsOn)}
        onTabClick={() => setMyFocusedAction(Action.Change_Status)}
        label="Change Status"
        tabHasError={tabHasErrorChangeStatus}
      />
    </>
  );

  const onRequestClose = () => {
    setIsOpen(false);

    // Set the container's batchActionsModalFocusedAction to undefined so that it
    // triggers a change event and reopens this modal if the same action is chosen back to back.
    // eslint-disable-next-line no-undefined
    setBatchActionsModalFocusedAction(undefined);
    setSwitchSendMsgIsOn(false);
    setSwitchLogCallIsOn(false);
    setSwitchChangeStatusIsOn(false);
    setSwitchAddNoteIsOn(false);
    setDisableSubmitControls(false);

    // Reset form status:
    setChangeStatusStateValues(initialStatusState);
    setAddNoteStateValues(initialNoteState);
    setLogCallStateValues(initialLogCallState);
    setSendMessageStateValues(initialMessageState);

    setTabHasErrorSendMsg(false);
    setTabHasErrorLogCall(false);
    setTabHasErrorAddNote(false);
    setTabHasErrorChangeStatus(false);

    setErrorMsgSendMsgEmail("");
    setErrorMsgSendMsgSms("");
    setErrorMsgSendMsgLetter("");
    setErrorMsgLogCall("");
    setErrorMsgAddNote("");
    setErrorMsgChangeStatus("");
  };

  const validateLogCallForm = (): boolean => {
    let hasFailedFormValidation = false;
    let errorMsg = "";
    setErrorMsgLogCall("");
    setTabHasErrorLogCall(false);
    if (switchLogCallIsOn) {
      if (!logCallStateValues.outcome) {
        hasFailedFormValidation = true;
        errorMsg = "Outcome is required. ";
      }
    }
    if (hasFailedFormValidation) {
      setErrorMsgLogCall(errorMsg);
      setTabHasErrorLogCall(true);
    }
    return hasFailedFormValidation;
  };

  const submitLogCall = () => {
    let prefix = "";

    if (logCallStateValues.reason) {
      prefix = `Reason: ${logCallStateValues.reason}. Outcome: ${logCallStateValues.outcome}`;
    } else {
      prefix = `Outcome: ${logCallStateValues.outcome}`;
    }

    const noteText = logCallStateValues.note;

    return logCall({
      variables: {
        interestId: interestID,
        reason: logCallStateValues.reason,
        outcome: logCallStateValues.outcome,
        note: `${prefix}${noteText ? " - " : ""}${noteText}`,
      },
    }).catch((err) => {
      logger.sendError(err);
      throw new Error("Error logging call. Please contact support@havenconnect.com for help.");
    });
  };

  const validateAddNoteForm = (): boolean => {
    let hasFailedFormValidation = false;
    let errorMsg = "";
    setErrorMsgAddNote("");
    setTabHasErrorAddNote(false);

    if (switchAddNoteIsOn) {
      if (!addNoteStateValues.note) {
        hasFailedFormValidation = true;
        errorMsg = "Note message is required.";
      }
    }

    if (hasFailedFormValidation) {
      setErrorMsgAddNote(errorMsg);
      setTabHasErrorAddNote(true);
    }

    return hasFailedFormValidation;
  };

  const submitAddNote = () =>
    createActivity({
      variables: {
        data: {
          resourceId: interestID,
          title: "Note",
          type: "note",
          resourceTable: "interests",
          note: addNoteStateValues.note || null,
        },
      },
    }).catch((err) => {
      logger.sendError(err);
      throw new Error("Error adding note. Please contact support@havenconnect.com for help.");
    });

  const validateSendMsgForm = (): boolean => {
    let hasFailedFormValidation = false;
    let errorMsgEmail = "";
    let errorMsgSms = "";
    let errorMsgLetter = "";

    const hasUseCases = interest.interestList.property.propertyUseCases.length > 0;

    setErrorMsgSendMsgEmail("");
    setErrorMsgSendMsgSms("");
    setErrorMsgSendMsgLetter("");
    setTabHasErrorSendMsg(false);

    if (switchSendMsgIsOn) {
      errorMsgEmail = "If Send an Email is enabled, ";
      if (sendMessageStateValues.emailEnabled) {
        if (!sendMessageStateValues.emailSubject) {
          errorMsgEmail += "Email subject is required. ";
          hasFailedFormValidation = true;
        }
        if (
          sendMessageStateValues.emailBccAddress &&
          !sendMessageStateValues.emailBccAddress.match(/@/)
        ) {
          errorMsgEmail += "A valid BCC email address is required. ";
          hasFailedFormValidation = true;
        }

        const emailBody = html(sendMessageStateValues.emailBody, TemplateType.EMAIL);
        if (!emailBody) {
          errorMsgEmail += "Email message is required. ";
          hasFailedFormValidation = true;
        }
      }

      if (sendMessageStateValues.smsEnabled) {
        if (!sendMessageStateValues.smsBody) {
          errorMsgSms += " If Send a Text is enabled, a message is required.";
          hasFailedFormValidation = true;
        }

        if (hasUseCases && sendMessageStateValues.smsPropertyUseCaseId === null) {
          errorMsgSms += " If Send a Text is enabled, a use case is required.";
          hasFailedFormValidation = true;
        }
      }

      const letterBody = html(sendMessageStateValues.letterBody, TemplateType.LETTER);
      if (sendMessageStateValues.letterEnabled && !letterBody) {
        errorMsgLetter = "If Send a Letter is enabled, a letter message is required.";
        hasFailedFormValidation = true;
      }
    }
    if (hasFailedFormValidation) {
      setErrorMsgSendMsgEmail(errorMsgEmail);
      setErrorMsgSendMsgSms(errorMsgSms);
      setErrorMsgSendMsgLetter(errorMsgLetter);
      setTabHasErrorSendMsg(true);
    }
    return hasFailedFormValidation;
  };

  const submitSendMsg = () =>
    sendTemplateMessages({
      variables: {
        interestListIds: [interestListID],
        interestIds: [interestID],
        elasticQuery: null,
        emailEnabled: sendMessageStateValues.emailEnabled,
        emailBody: html(sendMessageStateValues.emailBody, TemplateType.EMAIL),
        emailSubject: sendMessageStateValues.emailSubject,
        emailBccAddress: sendMessageStateValues.emailBccAddress || null,
        emailRespondToEnabled: sendMessageStateValues.emailRespondToEnabled,
        letterEnabled: sendMessageStateValues.letterEnabled,
        letterBody: html(sendMessageStateValues.letterBody, TemplateType.LETTER),
        smsEnabled: sendMessageStateValues.smsEnabled,
        smsBody: html(sendMessageStateValues.smsBody, TemplateType.SMS),
        smsPropertyUseCaseId: sendMessageStateValues.smsPropertyUseCaseId,
      },
    }).catch((err) => {
      logger.sendError(err);
      throw new Error(
        "Error sending message. Please don't retry in case part of the batch has been sent." +
          " Contact support@havenconnect.com",
      );
    });

  const validateChangeStatusForm = (): boolean => {
    let hasFailedFormValidation = false;
    let errorMsg = "";
    setErrorMsgChangeStatus("");
    setTabHasErrorChangeStatus(false);
    if (switchChangeStatusIsOn) {
      if (!changeStatusStateValues.reason && changeStatusStateValues.status !== "Moved In") {
        hasFailedFormValidation = true;
        errorMsg = "Reason is required.";
      }
    }
    if (hasFailedFormValidation) {
      setErrorMsgChangeStatus(errorMsg);
      setTabHasErrorChangeStatus(true);
    }
    return hasFailedFormValidation;
  };

  const submitChangeStatus = () =>
    changeStage({
      variables: {
        interest: interestID,
        unitNumber: changeStatusStateValues.unitNumber,
        actionDate: changeStatusStateValues.actionDate,
        status: changeStatusStateValues.status,
        reason: changeStatusStateValues.reason || null,
      },
    }).catch((err) => {
      logger.sendError(err);
      throw new Error("Error changing status. Please contact support@havenconnect.com for help.");
    });

  const validateForms = (): boolean => {
    let hasPassedFormValidation = true;
    const formsWithErrors = [];

    if (validateSendMsgForm()) {
      formsWithErrors.push("Send a Message form");
      hasPassedFormValidation = false;
    }

    if (validateLogCallForm()) {
      formsWithErrors.push("Log a Call form");
      hasPassedFormValidation = false;
    }

    if (validateAddNoteForm()) {
      formsWithErrors.push("Add a Note form");
      hasPassedFormValidation = false;
    }

    if (validateChangeStatusForm()) {
      formsWithErrors.push("Change Status form");
      hasPassedFormValidation = false;
    }

    if (!hasPassedFormValidation) {
      if (formsWithErrors.length > 1) {
        formsWithErrors[formsWithErrors.length - 1] =
          `and ${formsWithErrors[formsWithErrors.length - 1]}`;
      }
      openSnackbar({ message: `Error in ${formsWithErrors.join(", ")}`, status: "error", duration: errorBannerHideAfterMs });
    }

    return hasPassedFormValidation;
  };

  const submitAllActions = () => {
    const submitPromises = [];

    const hasPassedFormValidation = validateForms();
    if (!hasPassedFormValidation) {
      return;
    }

    setDisableSubmitControls(true);

    if (switchChangeStatusIsOn) {
      const changeStatusPromise = submitChangeStatus();
      submitPromises.push(changeStatusPromise);
    }

    if (switchLogCallIsOn) {
      const logCallPromise = submitLogCall();
      submitPromises.push(logCallPromise);
    }

    if (switchAddNoteIsOn) {
      const addNotePromise = submitAddNote();
      submitPromises.push(addNotePromise);
    }

    if (switchSendMsgIsOn) {
      const sendMsgPromise = submitSendMsg();
      submitPromises.push(sendMsgPromise);
    }

    Promise.all(submitPromises)
      .then(() => {
        openSnackbar({ message: "Success", status: "success" });
      })
      .catch((err) => {
        logger.sendError(err);
      })
      .finally(onRequestClose);
  };

  return (
    <Modal
      title="Batch Actions for 1 Applicant:"
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      fullScreen
      fullWidth
      scroll="paper"
    >
      <DialogContent>
        <Grid container spacing={2}>
          {renderTabsView(myFocusedAction)}

          {renderActionsViews(myFocusedAction)}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          id="batch-actions-cancel"
          color="secondary"
          size="L"
          onClick={onRequestClose}
          marginRight
          disabled={disableSubmitControls}
        >
            Cancel
        </Button>
        <Button
          id="batch-actions-send"
          color="primary"
          size="L"
          onClick={submitAllActions}
          disabled={disableSubmitControls}
        >
            Send
        </Button>
      </DialogActions>
    </Modal>
  );
};
export default BatchActionsModal;
